import {Injectable} from '@angular/core';
import {
    BaseItem,
    Department,
    DeviceItem, DeviceItemInList, DeviceLocation,
    DeviceTypeItem, FullDevice, InitDeviceResponse, InitLocationResponse, LogHistory,
    SearchData, ServerAnswer, ServerResult,
    ServerResults,
    SmallSelectItem,
    SystemTree,
    UploadedFile,
    User,
    UserSettings
} from './common-datatypes';
import {AppConfigService} from "./app-config.service";
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {StateStorageService} from './state-storage.service';
import {Observable} from 'rxjs';
import {of} from 'rxjs/internal/observable/of';
import {ToastController} from '@ionic/angular';
import * as dayjs from "dayjs";
import * as utc from "dayjs/plugin/utc";

dayjs.extend(utc)
import * as timezone from "dayjs/plugin/timezone";

dayjs.extend(timezone);
dayjs.tz.guess();
import * as customParseFormat from "dayjs/plugin/customParseFormat";
import {CommonFunctionsService} from "./common-functions.service";

dayjs.extend(customParseFormat);

export interface IGetSimilar {
    (value: string, parentvalue: string, numResults: number): Promise<ServerResults<string>>;
}

class Cache {
    department: SmallSelectItem[] = [];
    departmentWritable: SmallSelectItem[] = [];
    deviceStatus: SmallSelectItem[] = [];
    locationStatus: SmallSelectItem[] = [];
    ownership: SmallSelectItem[] = [];
    deviceSort: SmallSelectItem[] = [];
    deviceDefaultSort: SmallSelectItem = new SmallSelectItem();
    userRoles: SmallSelectItem[] = [];

    constructor() {
        this.reset();
    }

    reset(name: string = '') {
        if (('' === name) || ('department' === name)) this.department = [];
        if (('' === name) || ('departmentWritable' === name)) this.departmentWritable = [];
        if (('' === name) || ('deviceStatus' === name)) this.deviceStatus = [];
        if (('' === name) || ('locationStatus' === name)) this.locationStatus = [];
        if (('' === name) || ('ownership' === name)) this.ownership = [];
        if (('' === name) || ('deviceSort' === name)) this.deviceSort = [];
        if (('' === name) || ('deviceDefaultSort' === name)) this.deviceDefaultSort = new SmallSelectItem();
        if (('' === name) || ('userRoles' === name)) this.userRoles = [];
    }

}


@Injectable({
    providedIn: 'root'
})
export class RestDataService {
    private apiEndpoint = '';

    private cache = new Cache();

    constructor(private httpClient: HttpClient,
                private config: AppConfigService,
                private appState: StateStorageService,
                private toastController: ToastController,
                private commonFunctions: CommonFunctionsService) {
        this.apiEndpoint = this.config.getAppConfig('system.apiEndpoint', '');
        appState.loggedIn$.subscribe((res: boolean) => {
            if (!res) return;
            this.getUserRoles().then((data: SmallSelectItem[]) => {
                this.appState.userRoles = data;
                this.appState.userRolesChange = true;
            });
            this.getDefaultSort('invnr').then((data: SmallSelectItem) => {
                this.appState.defaultSearchSort = data;
            });
        });

    }

    private testServerResults(result: any): boolean {
        if (!result) return false;
        let header = '';
        let message = '';
        if (result.hasOwnProperty('error') && (typeof result.error === 'string')) {  // unknown error
            header = result.error;
            if (result.hasOwnProperty('message') && (typeof result.message === 'string')) message = result.message;
        } else if (result.hasOwnProperty('error') && result.error.hasOwnProperty('error')
            && (typeof result.error.error === 'string')) { // maybe ServerError
            header = result.error.error;
            if (result.error.hasOwnProperty('message') && (typeof result.error.message === 'string'))
                message = result.error.message;
        }

        if (header !== '') {
            this.commonFunctions.showHttpError(result, header + ' : ' + message);
            return false;
        } else return true;
    }

    public resetCache(value: string = '') {
        this.cache.reset(value);
    }

    /*
    Note: all dates and times are in UTC in the database
          the get converted to local time here after retrieving
          and back to utc here before saving
          all the rest of the Frontend uses by this local times without further conversion

          using dayjs.format() allways returns ISO8601 without fraction seconds
          https://day.js.org/docs/en/display/format
     */

    private fromUtcDateTimeToLocalString(utcDate: string | null): string | null {
        // this is a full datetime value, so conversion is easy
        if (!utcDate) return utcDate;
        return dayjs(utcDate).utc(true).local().format();
    }

    private fromUtcDateToLocalString(utcDate: string | null): string | null {
        // this is a date only value, by default it  is set to beginning of day
        // converting might change to the day before or after,
        // so just format the date without conversion
        // obsolete if no concersion is done if (!utcDate) return utcDate;
        //       return dayjs(utcDate, 'YYYY-MM-DD').utc(true).local().format('YYYY-MM-DD');
        return utcDate;
    }

    private fromLocalDateTimeStringToUtc(date: string | null): string | null {
        // this is special
        // as we are not using timepicker in input we extend the locale datetime with
        // the current time
        if (!date) return null; // db needs null not empty string
        const now = dayjs()
        return dayjs(date).hour(now.hour()).minute(now.minute()).second(now.second()).utc(false).format().substring(0,19); // omit trailing timezone;
    }

    private fromLocalDateStringToUtc(date: string | null): string | null {
        // see above
        if (!date) return null; // db needs null not empty string
//        return dayjs(date, 'YYYY-MM-DD').utc(false).format('YYYY-MM-DD');
        return date.substring(0,19); // omit trailing timezone;
    }

    private singleDeviceItemFromUtc(data: DeviceItem): DeviceItem {
        data.last_edit = <string>this.fromUtcDateTimeToLocalString(data.last_edit);
        data.buy_date = this.fromUtcDateToLocalString(data.buy_date);
        data.leasing_end = this.fromUtcDateToLocalString(data.leasing_end);
        data.tuev_date = this.fromUtcDateToLocalString(data.tuev_date);
        return data;
    }

    // Note location dates are datetime in database and in frontend
    // but editing will be restricted to date level with time defaulting to
    // now for same day editing and
    // start of day until end of day for reservations
    private singleDeviceItemInListFromUtc(data: DeviceItemInList): DeviceItemInList {
        data.last_edit = <string>this.fromUtcDateTimeToLocalString(data.last_edit);
        data.buy_date = this.fromUtcDateToLocalString(data.buy_date);
        data.leasing_end = this.fromUtcDateToLocalString(data.leasing_end);
        data.tuev_date = this.fromUtcDateToLocalString(data.tuev_date);
        data.start_date = this.fromUtcDateTimeToLocalString(data.start_date);
        data.end_date = this.fromUtcDateTimeToLocalString(data.end_date);
        return data;
    }

    private singleLocationFromUtc(data: DeviceLocation): DeviceLocation {
        data.last_edit = <string>this.fromUtcDateTimeToLocalString(data.last_edit);
        data.start_date = this.fromUtcDateTimeToLocalString(data.start_date);
        data.end_date = this.fromUtcDateTimeToLocalString(data.end_date);

        if (null === data.cancelled) data.cancelled = false;

        return data;
    }

    private singleLogHistoryFromUtc(data: LogHistory): LogHistory {
        data.date = <string>this.fromUtcDateTimeToLocalString(data.date);
        return data;
    }

    private fullDeviceFromUtc(data: ServerResults<FullDevice>): ServerResults<FullDevice> {
        data.items.forEach(item => {
            item.device = this.singleDeviceItemFromUtc(item.device);
            item.current_location = this.singleLocationFromUtc(item.current_location);
            item.former_locations.forEach(location => this.singleLocationFromUtc(location));
            item.reservations.forEach(location => this.singleLocationFromUtc(location));
        });
        return data;
    }

    private deviceLocationFromUtc(data: ServerResults<DeviceLocation>): ServerResults<DeviceLocation> {
        data.items.forEach(item => this.singleLocationFromUtc(item));
        return data;
    }

    private deviceItemInListFromUtc(data: ServerResults<DeviceItemInList>): ServerResults<DeviceItemInList> {
        data.items.forEach(item => this.singleDeviceItemInListFromUtc(item));
        return data;
    }

    private logHistoryFromUtc(data: ServerResults<LogHistory>): ServerResults<LogHistory> {
        data.items.forEach(item => this.singleLogHistoryFromUtc(item));
        return data;
    }

    // rest calls -----------------------

    public getCategoryList(): Promise<ServerResults<DeviceTypeItem>> {
        return this.getFromApiAfterLogin('/devicetypelist')
            .then((data) => <ServerResults<DeviceTypeItem>>data);
    }

    public getCategories(cat0Id: number, cat1Id: number, cat2Id: number, cat3Id: number,
                         search: string): Promise<DeviceTypeItem[]> {
        // Todo cache if not search
        const searchParams = new HttpParams()
            .set('c0', cat0Id && cat0Id.toString())
            .set('c1', cat1Id && cat1Id.toString())
            .set('c2', cat2Id && cat2Id.toString())
            .set('c3', cat3Id && cat3Id.toString())
            .set('s', search);

        return this.getFromApiAfterLogin('/devicetypes', searchParams)
            .then((data) => (<ServerResults<DeviceTypeItem>>data).items);
    }

    public getCategoriesByName(cat0: string, cat1: string, cat2: string, cat3: string): Promise<DeviceTypeItem[]> {
        // Todo cache if not search
        const searchParams = new HttpParams()
            .set('c0', cat0)
            .set('c1', cat1)
            .set('c2', cat2)
            .set('c3', cat3);
        return this.getFromApiAfterLogin('/devicetypesbyname', searchParams)
            .then((data) => (<ServerResults<DeviceTypeItem>>data).items);
    }

    public getDepartments(writableDepartment: boolean = false): Promise<SmallSelectItem[]> {
        if (writableDepartment && (0 < this.cache.departmentWritable.length))
            return <Promise<SmallSelectItem[]>>of(this.cache.departmentWritable).toPromise();
        else if (!writableDepartment && (0 < this.cache.department.length))
            return <Promise<SmallSelectItem[]>>of(this.cache.department).toPromise();
        // not in cache
        const searchParams = new HttpParams().set('wr', this.boolToIntString(writableDepartment));
        return this.getFromApiAfterLogin('/departments', searchParams)
            .then((data) => {
                const items = (<ServerResults<SmallSelectItem>>data).items;
                if (0 < items.length) {
                    if (writableDepartment) this.cache.departmentWritable = items;
                    else this.cache.department = items;
                }
                return items;
            });
    }

    public getDepartmentId(name: string): Promise<number> {
        // assume non-writeable departments are the superset
        return this.getDepartments()
            .then((data: SmallSelectItem[]) => {
                for (let i = 0; i < data.length; i++) {
                    if (data[i].name === name) return <number>data[i].id;
                }
                return BaseItem.UNDEFINED_ID;
            });
    }

    public getAllDepartments(): Promise<ServerResults<Department>> {
        return this.getFromApiAfterLogin('/alldepartments')
            .then((data) => <ServerResults<Department>>data);
    }

    public saveDepartment(department: Department): Promise<Department> {
        return this.putToApiAfterLogin('/department/' + department.id, {
            selectable: department.selectable, writable: department.writable
        })
            .then((data) => {
                this.resetCache('department');
                this.resetCache('departmentWritable');
                this.testServerResults(data);
                return (<ServerResult<Department>>data).item;
            });
    }

    public initDepartment(name: string): Promise<Department> {
        return this.postToApiAfterLogin('/departmentinit', {name: name})
            .then((data) => (<ServerResult<Department>>data).item);
    }

    public getDeviceStatus(): Promise<SmallSelectItem[]> {
        if (0 < this.cache.deviceStatus.length) return <Promise<SmallSelectItem[]>>of(this.cache.deviceStatus).toPromise();
        // not in cache
        return this.getFromApiAfterLogin('/devicestatus')
            .then((data) => {
                const items = (<ServerResults<SmallSelectItem>>data).items;
                if (0 < items.length) this.cache.deviceStatus = items;
                return items;
            });
    }

    public getDeviceStatusId(name: string): Promise<number> {
        return this.getDeviceStatus()
            .then((data: SmallSelectItem[]) => {
                for (let i = 0; i < data.length; i++) {
                    if (data[i].name === name) return <number>data[i].id;
                }
                return BaseItem.UNDEFINED_ID;
            });
    }

    public getLocationStatus(): Promise<SmallSelectItem[]> {
        if (0 < this.cache.locationStatus.length) return <Promise<SmallSelectItem[]>>of(this.cache.locationStatus).toPromise();
        // not in cache
        return this.getFromApiAfterLogin('/locationstatus')
            .then((data) => {
                const items = (<ServerResults<SmallSelectItem>>data).items;
                if (0 < items.length) this.cache.locationStatus = items;
                return items;
            });
    }

    public getLocationStatusId(name: string): Promise<number> {
        return this.getLocationStatus()
            .then((data: SmallSelectItem[]) => {
                for (let i = 0; i < data.length; i++) {
                    if (data[i].name === name) return <number>data[i].id;
                }
                return BaseItem.UNDEFINED_ID;
            });
    }

    public getOwnership(): Promise<SmallSelectItem[]> {
        if (0 < this.cache.ownership.length) return <Promise<SmallSelectItem[]>>of(this.cache.ownership).toPromise();
        // not in cache
        return this.getFromApiAfterLogin('/ownership')
            .then((data) => {
                const items = (<ServerResults<SmallSelectItem>>data).items;
                if (0 < items.length) this.cache.ownership = items;
                return items;
            });
    }

    public getDeviceSort(): Promise<SmallSelectItem[]> {
        if (0 < this.cache.deviceSort.length) return <Promise<SmallSelectItem[]>>of(this.cache.deviceSort).toPromise();
        // not in cache
        return this.getFromApiAfterLogin('/devicesort')
            .then((data) => {
                const items = (<ServerResults<SmallSelectItem>>data).items;
                if (0 < items.length) this.cache.deviceSort = items;
                return items;
            });
    }

    public getDefaultSort(name: string): Promise<SmallSelectItem> {
        return this.getDeviceSort().then((sortPossibilites: SmallSelectItem[]) => {
            if (0 < sortPossibilites.length) return new SmallSelectItem();
            let defaultSort = null;
            let invSort = null;
            sortPossibilites.forEach((sortItem: SmallSelectItem) => {
                if (name === sortItem.name) defaultSort = sortItem;
                if ('invnr' === sortItem.name) invSort = sortItem;
            });
            if (null !== defaultSort) return defaultSort;
            if (null !== invSort) return invSort;
            return sortPossibilites[0];
        });
    }

    public getUserRoles(): Promise<SmallSelectItem[]> {
        if (0 < this.cache.userRoles.length) return <Promise<SmallSelectItem[]>>of((this.cache.userRoles)!).toPromise();
        // not in cache
        return this.getFromApiAfterLogin('/userroles')
            .then((data) => {
                const items = (<ServerResults<SmallSelectItem>>data).items;
                if (0 < items.length) this.cache.userRoles = items;
                return items;
            });
    }

    public getOldUserNames(oldName: string): Observable<string> {
        const searchParams = new HttpParams()
            .set('on', oldName);
        // do not wait for login
        if ('' === this.apiEndpoint) { // sometimes not set yet during construction
            this.apiEndpoint = this.config.getAppConfig('system.apiEndpoint', '');
        }
        if ('' === this.apiEndpoint) return of('');
        return this.httpClient.get<string>(this.apiEndpoint + '/loginoldname',
            {params: searchParams, responseType: 'json'});
    }

    public generateThumbFile(fileId: string): Promise<UploadedFile> {
        const params = new HttpParams().set('longRequest', 'true');
        return this.postToApiAfterLogin('/genthumbfile', {fileId: fileId}, params)
            .then((data) => (<ServerResult<UploadedFile>>data).item);
    }

    public uploadFile(file: File, deviceId: string, invNr: string): Promise<UploadedFile> {
        const params = new HttpParams()
            .set('id', deviceId)
            .set('inv', invNr)
            .set('cfn', file.name)
            .set('mt', file.type)
            .set('longRequest', 'true');
        const headers = new HttpHeaders()
            .set('Content-Type', file.type);
        return this.postBinaryToApiAfterLogin('/uploadfile', file, params, headers)
            .then((data) => {
                const item = (<ServerResult<UploadedFile>>data).item;
                return this.generateThumbFile(item.id).then(
                    (thumbData: UploadedFile) => {
                        if (thumbData) item.thumb = thumbData.thumb;
                        return item;
                    });
            });
    }

    public addFile(fileId: string, deviceId: string): Promise<number> {
        return this.postToApiAfterLogin('/addfile',
            {deviceId: deviceId, fileId: fileId})
            .then((data) => (<ServerResult<number>>data).item);
    }

    public removeFile(fileId: string, deviceId: string): Promise<void> {
        return this.postToApiAfterLogin('/deletefile',
            {deviceId: deviceId, fileId: fileId})
            .then();
    }

    public initDevice(invnr: string | null): Promise<InitDeviceResponse> {
        if ('' === invnr) invnr = null;
        return this.postToApiAfterLogin('/deviceinit', {invnr: invnr})
            .then((data) => <InitDeviceResponse>data);
    }

    // sends DeviceItem but returns FullDevice !!
    public saveDevice(device: DeviceItem): Promise<ServerResults<FullDevice>> {
        return this.putToApiAfterLogin('/device/id/' + device.id,
            {
                invnr: device.invnr,
                ean: device.ean,
                manufacturer: device.manufacturer,
                model: device.model,
                serial: device.serial,
                specification: device.specification,
                accessories: device.accessories,
                url: device.url,
                buy_date: this.fromLocalDateStringToUtc(device.buy_date),
                leasing_end: this.fromLocalDateStringToUtc(device.leasing_end),
                ownership_id: device.ownership_id,
                device_status_id: device.device_status_id,
                price_netto: device.price_netto,
                price_brutto: device.price_brutto,
                comment: device.comment,
                tuev_date: this.fromLocalDateStringToUtc(device.tuev_date),
                type_id: device.type_id,
                vendor: device.vendor,
                owner: device.owner,
                department_id: device.department_id,
                sap_nr: device.sap_nr
            })
            .then((data) => this.fullDeviceFromUtc(<ServerResults<FullDevice>>data));
    }

    public initLocation(deviceId: string): Promise<InitLocationResponse> {
        return this.postToApiAfterLogin('/locationinit', {device_id: deviceId})
            .then((data) => <InitLocationResponse>data);
    }

    public saveLocation(deviceId: string, location: DeviceLocation): Promise<DeviceLocation> {
        return this.putToApiAfterLogin('/location/' + location.id,
            {
                device_id: deviceId,
                cancelled: location.cancelled,
                location: location.location,
                location_status_id: location.location_status_id,
                start_date: this.fromLocalDateTimeStringToUtc(location.start_date),
                end_date: this.fromLocalDateTimeStringToUtc(location.end_date),
                giver: location.giver,
                giver_email: location.giver_email,
                taker: location.taker,
                taker_email: location.taker_email,
                taker_address: location.taker_address,
                description: location.description
            })
            .then((data) => this.singleLocationFromUtc(<DeviceLocation>data));
    }

    public saveCategory(cat_0: string, cat_1: string, cat_2: string, cat_3: string): Promise<void> {
        return this.postToApiAfterLogin('/adddevicetype',
            {cat_0: cat_0, cat_1: cat_1, cat_2: cat_2, cat_3: cat_3})
            .then();
    }

    private boolToIntString(value: boolean | number): string {
        if ((value === true) || (value > 0)) {
            return '1';
        }
        return '0';
    }

    private compactSelectItems(valueArray: SmallSelectItem[]): string {
        let compact = '';
        if (valueArray && (0 < valueArray.length)) {
            valueArray.forEach((item) => {
                compact += ',' + item.id;
            });
        }
        if (1 < compact.length) {
            compact = compact.substring(1);
        }
        return compact;
    }

    private encodeSearchData(searchData: SearchData, offset: number, count: number): HttpParams {
        let searchParams = new HttpParams()
            .set('of', offset.toString())
            .set('cn', count.toString());
        if (searchData.simple) {
            searchParams = searchParams
                .set('ty', this.boolToIntString(true))
                .set('fs', searchData.simpleSearch.search)
                .set('ou', this.boolToIntString(searchData.onlyUsable))
                .set('od', this.boolToIntString(searchData.onlyDepartment))
                .set('dp', this.appState.authData.department ?? '')
                .set('so', this.compactSelectItems(searchData.simpleSort.sortItems))
                .set('sd', searchData.simpleSort.direction);
        } else {
            searchParams = searchParams
                .set('ty', this.boolToIntString(false))
                .set('fs', searchData.freeSearch.search)
                .set('fr', this.boolToIntString(!searchData.freeSearch.exact))
                .set('c0', searchData.category.cat_0_id && searchData.category.cat_0_id.toString())
                .set('c1', searchData.category.cat_1_id && searchData.category.cat_1_id.toString())
                .set('c2', searchData.category.cat_2_id && searchData.category.cat_2_id.toString())
                .set('c3', searchData.category.cat_3_id && searchData.category.cat_3_id.toString())
                .set('dp', this.compactSelectItems(searchData.department))
                .set('st', this.compactSelectItems(searchData.deviceStatus))
                .set('ls', this.compactSelectItems(searchData.locationStatus))
                .set('lo', searchData.location.search)
                .set('lr', this.boolToIntString(!searchData.location.exact))
                .set('ow', this.compactSelectItems(searchData.ownership))
                .set('bd', this.fromLocalDateStringToUtc(searchData.date.from) + ',' + this.fromLocalDateStringToUtc(searchData.date.to))
                .set('so', this.compactSelectItems(searchData.sort.sortItems))
                .set('sd', searchData.sort.direction);
        }
        return searchParams;
    }

    public getDevices(searchData: SearchData, offset: number, count: number): Promise<ServerResults<DeviceItemInList>> {
        const searchParams = this.encodeSearchData(searchData, offset, count);
        return this.getFromApiAfterLogin('/devices', searchParams)
            .then((data) => this.deviceItemInListFromUtc(<ServerResults<DeviceItemInList>>data));
    }

    public getFullDevices(searchData: SearchData, offset: number, count: number): Promise<ServerResults<FullDevice>> {
        const searchParams = this.encodeSearchData(searchData, offset, count);
        return this.getFromApiAfterLogin('/fulldevices', searchParams)
            .then((data) => this.fullDeviceFromUtc(<ServerResults<FullDevice>>data));
    }

    public getFullDeviceByInvNr(invNr: string): Promise<ServerResults<FullDevice>> {
        return this.getFromApiAfterLogin('/device/inv/' + invNr)
            .then((data) => this.fullDeviceFromUtc(<ServerResults<FullDevice>>data));
    }

    public getFullDeviceById(id: string): Promise<ServerResults<FullDevice>> {
        return this.getFromApiAfterLogin('/device/id/' + id)
            .then((data) => this.fullDeviceFromUtc(<ServerResults<FullDevice>>data));
    }

    public getDeviceLogsById(id: string): Promise<ServerResults<LogHistory>> {
        const searchParams = new HttpParams()
            .set('id', id);
        return this.getFromApiAfterLogin('/devicelogs', searchParams)
            .then((data) => this.logHistoryFromUtc(<ServerResults<LogHistory>>data));
    }

    public getLocationLogsById(id: string): Promise<ServerResults<LogHistory>> {
        const searchParams = new HttpParams()
            .set('id', id);
        return this.getFromApiAfterLogin('/locationlogs', searchParams)
            .then((data) => this.logHistoryFromUtc(<ServerResults<LogHistory>>data));
    }

    public getUserLogsById(id: number): Promise<ServerResults<LogHistory>> {
        const searchParams = new HttpParams()
            .set('id', id.toString())
            .set('longRequest', 'true');
        return this.getFromApiAfterLogin('/userlogs', searchParams)
            .then((data) => this.logHistoryFromUtc(<ServerResults<LogHistory>>data));
    }

    public getSimilarInvNr(value: string, parentvalue /*unused*/: string = '', numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('inv', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/invnumber', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSameSapNr(value: string, parentvalue /*unused*/: string = '', numResults: number = 2): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('sap', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/sapnumber', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarManufacturers(value /*manufacturer*/: string, parentvalue /*unused*/: string = '', numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('man', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/manufacturers', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarModels(value /*model*/: string, parentvalue /*manufacturer*/: string, numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('man', parentvalue)
            .set('mod', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/models', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarVendors(value /*vendor*/: string, parentvalue /*unused*/: string = '', numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('vend', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/vendors', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarOwners(owner: string, parentvalue /*unused*/: string = '', numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('own', owner)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/owners', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSystemTree(id: string): Promise<SystemTree> {
        const searchParams = new HttpParams()
            .set('id', id);
        return this.getFromApiAfterLogin('/systemtree', searchParams)
            .then((data) => <SystemTree>data);
    }

    public removeFromSystem(deviceID: string, parentID: string): Promise<void> {
        return this.postToApiAfterLogin('/removesystem',
            {parent_id: parentID, child_id: deviceID})
            .then();
    }

    public addToSystem(id: string, parentInvNr: string): Promise<void> {
        return this.postToApiAfterLogin('/addsystem',
            {invnr: parentInvNr, child_id: id})
            .then();
    }

    public getSimilarLocations(value /*location*/: string, parentvalue /*unused*/: string = '', numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('loc', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/locations', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarTaker(value /*taker*/: string, parentvalue /*unused*/: string = '', numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('tak', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/locationtakers', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarTakerEmail(value /*email*/: string, parentvalue /*taker*/: string, numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('tak', parentvalue)
            .set('em', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/locationtakeremails', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarTakerAddress(value /*address*/: string, parentvalue /*taker*/: string, numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('tak', parentvalue)
            .set('adr', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/locationtakeraddresses', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarGiver(value /*giver*/: string, parentvalue /*unused*/: string = '', numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('giv', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/locationgivers', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getSimilarGiverEmail(value /*email*/: string, parentvalue /*giver*/: string, numResults: number = 10): Promise<ServerResults<string>> {
        const searchParams = new HttpParams()
            .set('giv', parentvalue)
            .set('em', value)
            .set('lim', numResults);
        return this.getFromApiAfterLogin('/locationgiveremails', searchParams)
            .then((data) => <ServerResults<string>>data);
    }

    public getExport(searchData: SearchData): Promise<Blob> {
        const searchParams = this.encodeSearchData(searchData, 0, 0)
            .set('longRequest', 'true');
        return this.accessApiAfterLogin('/export', searchParams, null, 'get', 'blob' as 'json')
            .then((data) => <Blob>data);
    }

    // no utc data translation in user settings as those are frontend values
    public getUserSettings(): Promise<UserSettings> {
        return this.getFromApiAfterLogin('/usersettings')
            .then((data) => (<ServerResult<UserSettings>>data).item);
    }

    public setUserSettings(settings: UserSettings) {
        return this.putToApiAfterLogin('/usersettings', {settings: settings})
            .then((data) => (<ServerResult<UserSettings>>data).item);
    }

    public getUsers(): Promise<User[]> {
        return this.getFromApiAfterLogin('/userlist')
            .then((data) => (<ServerResults<User>>data).items);
    }

    public addUser(userId: string, userName: string, userDescription: string, userDepartmentId: number): Promise<User> {
        return this.postToApiAfterLogin('/userinit',
            {user_id: userId, user_name: userName, description: userDescription, department_id: userDepartmentId})
            .then((data) => (<ServerResult<User>>data).item);
    }

    public saveUser(user: User): Promise<User> {
        return this.putToApiAfterLogin('/user/' + user.id, {
            active: user.active, role_id_bits: user.role_id_bits, full_name: user.full_name,
            department_id: user.department_id, description: user.description
        })
            .then((data) => (<ServerResult<User>>data).item);
    }

    private postToApiAfterLogin(path: string, body: any, params: HttpParams = new HttpParams()): Promise<ServerAnswer> {
        return <Promise<ServerAnswer>>this.accessApiAfterLogin(path, params, body, 'post');
    }

    private postBinaryToApiAfterLogin(path: string, body: any, params: HttpParams = new HttpParams(), headers: HttpHeaders = new HttpHeaders()):
        Promise<ServerAnswer> {
        return <Promise<ServerAnswer>>this.accessApiAfterLogin(path, params, body, 'post', null, headers);
    }

    private putToApiAfterLogin(path: string, body: any): Promise<ServerAnswer> {
        return <Promise<ServerAnswer>>this.accessApiAfterLogin(path, new HttpParams(), body, 'put');
    }

    private deleteOnApiAfterLogin(path: string): Promise<ServerAnswer> {
        return <Promise<ServerAnswer>>this.accessApiAfterLogin(path, new HttpParams(), null, 'delete');
    }

    private getFromApiAfterLogin(path: string, searchParams: HttpParams = new HttpParams()): Promise<ServerAnswer> {
        return <Promise<ServerAnswer>>this.accessApiAfterLogin(path, searchParams, null, 'get');
    }

    private accessApiAfterLogin(path: string, params: HttpParams, body: any, method: string,
                                responseType: any = null, headers: HttpHeaders = new HttpHeaders()):
        Promise<ServerAnswer> {
        if (!params) params = new HttpParams();
        if (!responseType) responseType = 'json' as 'json';

        return new Promise((resolve, reject) => {
            if (this.appState.loggedIn) {
                this.accessApi(resolve, reject, path, params, body, method, responseType, headers);
            } else {
                this.appState.loggedIn$
                    .subscribe((loggedIn: boolean) => {
                        if (loggedIn) {
                            this.accessApi(resolve, reject, path, params, body, method, responseType, headers);
                        } else {
                            console.log('deferred', path);
                        }
                    });
            }
        });
    }

    private accessApi(resolve: Function, reject: Function, path: string, params: HttpParams, body: any, method: string, responseType: any, headers: HttpHeaders): void {
        if ('' === this.apiEndpoint) { // sometimes not set yet during construction
            this.apiEndpoint = this.config.getAppConfig('system.apiEndpoint', '');
        }
        const url = this.apiEndpoint + path;
        this.httpClient.request(method, url, {body: body, headers: headers, params: params, responseType: responseType})
            .subscribe({
                next: (res: any) => {
                    if (this.testServerResults(res)) resolve(<ServerAnswer>res); else reject(res)
                },
                error: (err: any) => {
                    this.testServerResults(err);
                    this.commonFunctions.showHttpError(err);
                    reject();
                },
            });
    }
}
